// Ubicación en el repo: src/components/CancelAppointmentModal.jsx
// Confirmación de cancelación cuando la cita tiene un paciente vinculado
// CON correo registrado (pedido de Nicolás, 19-ago-2026) — pide un motivo
// breve antes de cancelar, para incluirlo en el correo automático de aviso
// al paciente (ver Agenda.jsx handleDelete y Edge Function
// notify-appointment-cancellation). Si la cita no tiene paciente o el
// paciente no tiene correo, Agenda.jsx sigue usando el window.confirm()
// simple de siempre — este modal no aplica ahí.
//
// Error de agendamiento del staff (24-ago-2026, pedido de Nicolás): antes,
// CUALQUIER eliminación de una cita con paciente+correo mandaba sí o sí un
// correo de cancelación — incluso cuando el motivo real era "me equivoqué
// al agendar" y no había nada que avisarle al paciente (la hora ni
// siquiera era válida para él/ella). El checkbox de abajo separa ese caso:
// sigue quedando registrado el motivo en `cancellation_reason` (auditoría
// interna), pero se salta por completo el paso de enviar el correo.
import { useState } from 'react';
import { formatPatientName } from '../lib/format';

// Motivos predeterminados (pedido de Nicolás, 19-ago-2026) — más rápido de
// usar y da mensajes consistentes en el correo al paciente. "Otro" es el
// único que habilita el campo de texto libre.
const REASON_PRESETS = [
  'Licencia del profesional por problema de salud',
  'Problema logístico de la sucursal (equipamiento, corte de luz u otro imprevisto)',
  'Imprevisto personal del profesional',
  'Reorganización de horarios en la agenda',
  'Otro',
];

const STAFF_MISTAKE_REASON = 'Error al agendar (corregido por el equipo, sin aviso al paciente)';

export default function CancelAppointmentModal({ appointment, patient, onCancel, onConfirm, loading }) {
  const [staffMistake, setStaffMistake] = useState(false);
  const [reasonOption, setReasonOption] = useState(REASON_PRESETS[0]);
  const [customReason, setCustomReason] = useState('');

  const isOther = reasonOption === 'Otro';
  const finalReason = staffMistake ? STAFF_MISTAKE_REASON : isOther ? customReason.trim() : reasonOption;
  const canConfirm = staffMistake || !!finalReason;

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h2>Cancelar cita</h2>
        <p>
          <strong>{formatPatientName(patient)}</strong> tiene correo registrado
          (<strong>{patient.email}</strong>).{' '}
          {staffMistake
            ? 'No se le va a enviar ningún correo — se elimina directamente.'
            : 'Al cancelar, se le manda automáticamente un correo avisando la cancelación, con el motivo que indiques abajo.'}
        </p>

        <label className="checkbox-inline" style={{ marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={staffMistake}
            onChange={(e) => setStaffMistake(e.target.checked)}
          />
          Fue un error al agendar (agendamos algo mal) — no avisar al paciente
        </label>

        {!staffMistake && (
          <>
            <label className="field">
              <span>Motivo (se incluye en el correo al paciente)</span>
              <select value={reasonOption} onChange={(e) => setReasonOption(e.target.value)} autoFocus>
                {REASON_PRESETS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
            {isOther && (
              <label className="field">
                <span>Explica el motivo</span>
                <input
                  type="text"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Describe brevemente el motivo…"
                  autoFocus
                  maxLength={200}
                />
              </label>
            )}
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="button-secondary" onClick={onCancel} disabled={loading}>
            Volver
          </button>
          <button
            type="button"
            className="button-danger"
            disabled={!canConfirm || loading}
            onClick={() => onConfirm(finalReason, !staffMistake)}
          >
            {loading ? 'Eliminando…' : staffMistake ? 'Eliminar sin avisar' : 'Cancelar cita y avisar'}
          </button>
        </div>
      </div>
    </div>
  );
}
